import {
	Building2,
	ChevronDown,
	ChevronRight,
	ChevronsDownUp,
	ChevronsUpDown,
	Pencil,
	PlusIcon,
	Trash2,
} from 'lucide-react';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SubAsset } from '@/common/sub-asset';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ROLE } from '../../../common/role';
import { usePermission } from '../../../hooks/use-permission';
import { Restricted } from '../../Restricted';
import { Input } from '../../ui/input';

type AssetItemProps = {
	subAsset: SubAsset;
	onAddChild?: (parentId: string, name: string) => void;
	onEdit?: (id: string, newName: string) => void;
	/** Context-menu Delete (GH #331); the card confirms */
	onDelete?: (subAsset: SubAsset) => void;
	level?: number;
	viewOnlyMode?: boolean;
	onSelect?: (id: string) => void;
	selectedId?: string;
	defaultExpanded?: boolean;
};

/** The rename or add-child editor open in the tree; opening one closes the other. */
type TreeEditor = { id: string; kind: 'rename' | 'addChild' };
type TreeEditorState = { editor: TreeEditor | null; setEditor: (editor: TreeEditor | null) => void };

const TreeEditorContext = createContext<TreeEditorState | null>(null);

/** Keeps a single editor open across every tree rendered inside it. */
export function SubAssetTreeEditorProvider({ children }: { children: ReactNode }) {
	const [editor, setEditor] = useState<TreeEditor | null>(null);
	const value = useMemo(() => ({ editor, setEditor }), [editor]);
	return <TreeEditorContext.Provider value={value}>{children}</TreeEditorContext.Provider>;
}

function useTreeEditor() {
	const state = useContext(TreeEditorContext);
	if (!state) throw new Error('SubAssetNode must be rendered inside SubAssetTreeEditorProvider');
	return state;
}

/**
 * One node of the structure tree.
 *
 * Interaction: a single click selects the node (and opens/closes its children),
 * a double click or the right-click menu renames it, the arrow only toggles.
 * In `viewOnlyMode` (pickers) a click only selects.
 * A tree outside a SubAssetTreeEditorProvider gets its own.
 */
export function SubAssetItem(props: AssetItemProps) {
	const hasProvider = useContext(TreeEditorContext) !== null;
	const node = <SubAssetNode {...props} />;
	return hasProvider ? node : <SubAssetTreeEditorProvider>{node}</SubAssetTreeEditorProvider>;
}

function SubAssetNode({
	subAsset,
	onAddChild,
	onEdit,
	onDelete,
	level = 0,
	viewOnlyMode = false,
	onSelect,
	selectedId,
	defaultExpanded = false,
}: AssetItemProps) {
	const { t } = useTranslation();
	const isRoot = level === 0;
	const { editor, setEditor } = useTreeEditor();
	const isEditing = editor?.kind === 'rename' && editor.id === subAsset.id;
	const isAddingChild = editor?.kind === 'addChild' && editor.id === subAsset.id;
	const [editName, setEditName] = useState(subAsset.name || '');
	const [newChildName, setNewChildName] = useState('');
	const [isExpanded, setIsExpanded] = useState(defaultExpanded || isRoot);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const editInputRef = useRef<HTMLInputElement>(null);
	const childInputRef = useRef<HTMLInputElement>(null);
	const doesHaveChildren = !!subAsset.children?.length;
	const isSelected = subAsset.id === selectedId;

	const shouldUpdate = usePermission(ROLE.SUB_ASSETS_UPDATE);
	const canRename = shouldUpdate && !isRoot && !viewOnlyMode;
	// The root row is the asset itself; it is deleted from the asset form, not here
	const canDelete = usePermission(ROLE.SUB_ASSETS_DELETE) && !isRoot && !viewOnlyMode && !!onDelete;

	// Renaming starts with the whole name selected so typing replaces it
	// The open editor takes the focus, so typing, Enter and Escape reach it
	const focusEditor = () => {
		const input = editInputRef.current ?? childInputRef.current;
		input?.focus();
		input?.select();
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: focus once, when an editor opens
	useEffect(() => {
		if (isEditing || isAddingChild) focusEditor();
	}, [isEditing, isAddingChild]);

	const handleEdit = () => {
		if (!canRename || !subAsset.id) return;
		setEditName(subAsset.name || '');
		setEditor({ id: subAsset.id, kind: 'rename' });
	};

	const handleSaveEdit = () => {
		if (!subAsset.id) return;
		const name = editName.trim();
		if (name && name !== subAsset.name) onEdit?.(subAsset.id, name);
		setEditor(null);
	};

	const handleCancelEdit = () => {
		setEditName(subAsset.name || '');
		setEditor(null);
	};

	const handleAddChild = () => {
		if (viewOnlyMode || !subAsset.id) return;
		setEditor({ id: subAsset.id, kind: 'addChild' });
		setNewChildName('');
	};

	const handleSaveChild = () => {
		if (newChildName.trim() && subAsset.id) {
			onAddChild?.(subAsset.id, newChildName.trim());
			setIsExpanded(true);
		}
		setEditor(null);
	};

	const handleCancelChild = () => {
		setEditor(null);
	};

	const handleSelect = () => {
		if (onSelect && subAsset.id) onSelect(subAsset.id);
	};

	const toggleExpanded = () => {
		if (doesHaveChildren) setIsExpanded((value) => !value);
	};

	// Single click: select, and open/close the children. Pickers only select.
	const handleRowClick = () => {
		if (isEditing) return;
		handleSelect();
		if (!viewOnlyMode) toggleExpanded();
	};

	// Double click: rename (the two single clicks cancel each other's toggle).
	const handleRowDoubleClick = (e: React.MouseEvent) => {
		e.preventDefault();
		if (!canRename) return;
		handleEdit();
	};

	const handleContextMenu = (e: React.MouseEvent) => {
		if (viewOnlyMode || isEditing) return;
		e.preventDefault();
		handleSelect();
		setIsMenuOpen(true);
	};

	return (
		<div className="select-none">
			<DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
				{/** biome-ignore lint/a11y/noStaticElementInteractions: tree row with mouse interactions */}
				{/** biome-ignore lint/a11y/useKeyWithClickEvents: tree row with mouse interactions */}
				<div
					className={cn(
						'group flex items-center gap-2 p-2 rounded-md transition-colors relative text-card-foreground pl-4 hover:bg-background',
						{
							'pl-2 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-white hover:bg-zinc-0':
								!viewOnlyMode && isRoot,
							'bg-blue-100 dark:bg-blue-800 border border-blue-300 dark:border-blue-600 text-blue-900 dark:text-white hover:bg-blue-200':
								isSelected,
							'cursor-pointer': !isEditing,
						},
					)}
					onClick={handleRowClick}
					onDoubleClick={handleRowDoubleClick}
					onContextMenu={handleContextMenu}
				>
					{/* Invisible anchor for the right-click menu: it opens at the row's start */}
					<DropdownMenuTrigger asChild>
						<span className="absolute left-6 bottom-0 h-0 w-0" aria-hidden="true" />
					</DropdownMenuTrigger>
					<div className="flex w-full gap-2 rounded transition-colors items-center">
						{/** biome-ignore lint/a11y/noStaticElementInteractions: need for click */}
						{/** biome-ignore lint/a11y/useKeyWithClickEvents: need for click */}
						<span
							onClick={(e) => {
								e.stopPropagation();
								toggleExpanded();
							}}
							onDoubleClick={(e) => e.stopPropagation()}
							className={cn('p-0.5 hover:bg-accent rounded transition-colors invisible', {
								visible: doesHaveChildren,
							})}
						>
							{isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
						</span>
						<Building2 className="h-6 w-5" />
						<div className="flex w-full justify-between">
							{isEditing && (
								// biome-ignore lint/a11y/noStaticElementInteractions: keeps row handlers out of the editor
								// biome-ignore lint/a11y/useKeyWithClickEvents: keeps row handlers out of the editor
								<div
									className="flex items-center gap-2 flex-1"
									onClick={(e) => e.stopPropagation()}
									onDoubleClick={(e) => e.stopPropagation()}
								>
									<Input
										ref={editInputRef}
										value={editName}
										onChange={(e) => setEditName(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter') handleSaveEdit();
											if (e.key === 'Escape') handleCancelEdit();
										}}
										className="w-full rounded-md h-7 text-sm bg-white"
									/>
									<Button onClick={handleSaveEdit} className="rounded-md h-6 px-2">
										{t('common.actions.save')}
									</Button>
									<Button variant="outline" onClick={handleCancelEdit} className="rounded-md h-6 px-2">
										{t('common.actions.cancel')}
									</Button>
								</div>
							)}
							{!isEditing && (
								<>
									<div
										className="truncate flex-1"
										title={canRename ? t('assets.structure.doubleClickToRename') : undefined}
									>
										<span
											className={cn('text-sm font-medium text-card-foreground', {
												'text-blue-900 dark:text-primary-foreground': isRoot || isSelected,
											})}
										>
											{subAsset.name}
										</span>
									</div>
									<Restricted to={ROLE.SUB_ASSETS_CREATE}>
										{!viewOnlyMode && (
											<Button
												variant="default"
												onClick={(e) => {
													e.stopPropagation();
													handleAddChild();
												}}
												onDoubleClick={(e) => e.stopPropagation()}
												title={t('assets.structure.addChild')}
												className="text-sm font-medium h-6 px-1.5! py-0! opacity-0 group-hover:opacity-100 transition-all ml-2 rounded flex items-center gap-0.5"
											>
												<span className="text-xs font-bold leading-none mb-0.5">↳</span>
												<PlusIcon className="size-3" />
											</Button>
										)}
									</Restricted>
								</>
							)}
						</div>
					</div>
				</div>
				{!viewOnlyMode && (
					// While the menu fades out, the pointer still moves the focus onto its items. Once it is
					// gone, the editor it opened takes the focus back; without one, Radix returns it as usual.
					<DropdownMenuContent
						align="start"
						onCloseAutoFocus={(e) => {
							if (!editInputRef.current && !childInputRef.current) return;
							e.preventDefault();
							focusEditor();
						}}
					>
						<DropdownMenuItem onClick={handleEdit} disabled={!canRename}>
							<Pencil /> {t('common.actions.rename')}
						</DropdownMenuItem>
						<Restricted to={ROLE.SUB_ASSETS_CREATE}>
							<DropdownMenuItem onClick={handleAddChild}>
								<PlusIcon /> {t('assets.structure.addChild')}
							</DropdownMenuItem>
						</Restricted>
						{canDelete && (
							<DropdownMenuItem variant="destructive" onClick={() => onDelete?.(subAsset)}>
								<Trash2 /> {t('common.actions.delete')}
							</DropdownMenuItem>
						)}
						{doesHaveChildren && (
							<>
								<DropdownMenuSeparator />
								<DropdownMenuItem onClick={toggleExpanded}>
									{isExpanded ? (
										<>
											<ChevronsDownUp /> {t('assets.structure.collapse')}
										</>
									) : (
										<>
											<ChevronsUpDown /> {t('assets.structure.expand')}
										</>
									)}
								</DropdownMenuItem>
							</>
						)}
					</DropdownMenuContent>
				)}
			</DropdownMenu>
			<div className="border-l ml-4.5">
				{isExpanded &&
					doesHaveChildren &&
					subAsset.children?.map((child) => (
						<SubAssetNode
							key={child.id}
							subAsset={child}
							onAddChild={onAddChild}
							onEdit={onEdit}
							onDelete={onDelete}
							level={level + 1}
							viewOnlyMode={viewOnlyMode}
							onSelect={onSelect}
							selectedId={selectedId}
							defaultExpanded={defaultExpanded}
						/>
					))}
				{isAddingChild && (
					<div className="flex items-center gap-2 p-2 rounded-md transition-colors bg-blue-50 border border-blue-200 dark:bg-transparent dark:border-none pl-10">
						<div className="w-5" />
						<Building2 className="text-primary size-4" />
						<div className="flex-1">
							<Input
								ref={childInputRef}
								value={newChildName}
								onChange={(e) => setNewChildName(e.target.value)}
								placeholder={t('assets.structure.enterChildName')}
								className="w-full rounded-md h-7 text-sm bg-white"
								onKeyDown={(e) => {
									if (e.key === 'Enter') handleSaveChild();
									if (e.key === 'Escape') handleCancelChild();
								}}
							/>
						</div>
						<div className="flex items-center gap-1">
							<Button onClick={handleSaveChild} className="rounded-md h-6 px-2">
								{t('common.actions.save')}
							</Button>
							<Button variant="outline" onClick={handleCancelChild} className="rounded-md h-6 px-2">
								{t('common.actions.cancel')}
							</Button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
